import { useEffect, useRef } from "react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
// Poll every 60 seconds. Anything tighter is wasteful; anything looser
// makes long-running tabs feel stale after a deploy.
const POLL_INTERVAL_MS = 60_000;

/**
 * Polls /api/version every minute. When the backend reports a different
 * build identifier than what we first saw, raises a one-time "New version
 * available — Refresh" toast with a button that triggers a hard reload.
 *
 * Pairs with the no-cache meta tags on index.html: meta tags ensure the
 * HTML shell is always re-fetched; this hook covers users who keep the
 * tab open for hours/days without a navigation.
 *
 * Network errors are intentionally swallowed — we never want a backend
 * blip to surface as a noisy toast.
 */
export function useVersionPoll() {
  const seenVersion = useRef(null);
  const notified = useRef(false);

  useEffect(() => {
    let cancelled = false;

    async function check() {
      try {
        const res = await fetch(`${API}/api/version`, {
          cache: "no-store",
          headers: { "Cache-Control": "no-cache" },
        });
        if (!res.ok || cancelled) return;
        const body = await res.json();
        const v = body?.version;
        if (!v) return;
        if (seenVersion.current == null) {
          seenVersion.current = v;
          return;
        }
        if (v !== seenVersion.current && !notified.current) {
          notified.current = true;
          toast.message("New version available", {
            description: "Refresh to load the latest update.",
            duration: Infinity,
            action: {
              label: "Refresh",
              onClick: () => window.location.reload(),
            },
            // sonner adds a default close button; we want it visible so
            // users can dismiss without refreshing.
            closeButton: true,
          });
        }
      } catch {
        /* silent — network blips should never bother the user */
      }
    }

    check();
    const id = setInterval(check, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);
}
